import { SubjectData } from 'shell/models/person';
import { AccordionCivilUnion as CivilUnionVM } from 'shell/view-models/accordion-civil-union';
import { DateTime } from 'luxon';
import { transformToChoiceDate } from 'shell/components/shared/choice-date/functions';

export const transformCivilUnion = (person: SubjectData): CivilUnionVM | undefined => {
  const birthChoiceDate = transformToChoiceDate(
    person.unioneCivile?.unitoCivilmente?.senzaGiorno,
    person.unioneCivile?.unitoCivilmente?.senzaGiornoMese
  );

  const eventChoiceDate = transformToChoiceDate(
    person.unioneCivile?.attoUnione?.senzaGiorno,
    person.unioneCivile?.attoUnione?.senzaGiornoMese
  );

  const isDigital = Boolean(person.unioneCivile?.attoUnione?.attoEventoANPR?.attoANSC);

  const originReturnData: CivilUnionVM | undefined = person.unioneCivile
    ? {
        number: person.unioneCivile?.ordineUnione
          ? Number.parseInt(person.unioneCivile?.ordineUnione, 10)
          : 0,
        generality: {
          aire: person.unioneCivile?.unitoCivilmente?.soggettoAIRE,
          validityFiscalCode: person.unioneCivile?.unitoCivilmente?.codiceFiscale?.validitaCF,
          validityDateFiscalCode: person.unioneCivile?.unitoCivilmente?.codiceFiscale
            ?.dataAttribuzioneValidita
            ? DateTime.fromISO(
                person.unioneCivile?.unitoCivilmente?.codiceFiscale?.dataAttribuzioneValidita
              )
            : null,
          gender: person.unioneCivile?.unitoCivilmente?.sesso || '',
          expatriationYear: person.unioneCivile?.unitoCivilmente?.annoEspatrio || '',
          birthDate: person.unioneCivile?.unitoCivilmente?.dataNascita
            ? DateTime.fromISO(person.unioneCivile?.unitoCivilmente?.dataNascita)
            : null,
          // senza giorno mese, etc
          birthChoiceDate,
          // generality italian form
          italian:
            person.unioneCivile?.unitoCivilmente?.luogoNascita?.comune?.codiceIstat ||
            person.unioneCivile?.unitoCivilmente?.luogoNascita?.comune?.nomeComune ||
            person.unioneCivile?.unitoCivilmente?.luogoNascita?.comune?.siglaProvinciaIstat ||
            person.unioneCivile?.unitoCivilmente?.luogoNascita?.localita?.descrizioneLocalita
              ? {
                  province:
                    person.unioneCivile?.unitoCivilmente?.luogoNascita?.comune
                      ?.siglaProvinciaIstat || '',
                  municipality:
                    person.unioneCivile?.unitoCivilmente?.luogoNascita?.comune?.codiceIstat ||
                    person.unioneCivile?.unitoCivilmente?.luogoNascita?.comune?.nomeComune ||
                    person.unioneCivile?.unitoCivilmente?.luogoNascita?.comune?.siglaProvinciaIstat
                      ? {
                          id:
                            person.unioneCivile?.unitoCivilmente?.luogoNascita?.comune
                              ?.codiceIstat || '',
                          description:
                            person.unioneCivile?.unitoCivilmente?.luogoNascita?.comune
                              ?.nomeComune || '',
                          province:
                            person.unioneCivile?.unitoCivilmente?.luogoNascita?.comune
                              ?.siglaProvinciaIstat || '',
                        }
                      : null,
                }
              : undefined,
          // generality foreign form
          foreign:
            person.unioneCivile?.unitoCivilmente?.luogoNascita?.localita?.codiceStato ||
            person.unioneCivile?.unitoCivilmente?.luogoNascita?.localita?.descrizioneStato ||
            person.unioneCivile?.unitoCivilmente?.luogoNascita?.localita?.provinciaContea ||
            person.unioneCivile?.unitoCivilmente?.luogoNascita?.localita?.descrizioneLocalita
              ? {
                  state:
                    person.unioneCivile?.unitoCivilmente?.luogoNascita?.localita?.codiceStato ||
                    person.unioneCivile?.unitoCivilmente?.luogoNascita?.localita?.descrizioneStato
                      ? {
                          id:
                            person.unioneCivile?.unitoCivilmente?.luogoNascita?.localita
                              ?.codiceStato || '',
                          value:
                            person.unioneCivile?.unitoCivilmente?.luogoNascita?.localita
                              ?.descrizioneStato || '',
                        }
                      : null,
                  province:
                    person.unioneCivile?.unitoCivilmente?.luogoNascita?.localita?.provinciaContea ||
                    '',
                  municipality: person.unioneCivile?.unitoCivilmente?.luogoNascita?.localita
                    ?.descrizioneLocalita
                    ? {
                        description:
                          person.unioneCivile?.unitoCivilmente?.luogoNascita?.localita
                            ?.descrizioneLocalita || '',
                        province:
                          person.unioneCivile?.unitoCivilmente?.luogoNascita?.localita
                            ?.provinciaContea || '',
                      }
                    : null,
                }
              : undefined,
          // Other fields generality
          exceptionalBirthPlace:
            person.unioneCivile?.unitoCivilmente?.luogoNascita?.luogoEccezionale || '',
          municipalityIdentifier:
            person.unioneCivile?.unitoCivilmente?.idSchedaSoggettoComune?.idSchedaSoggetto || '',
          istatCode:
            person.unioneCivile?.unitoCivilmente?.idSchedaSoggettoComune
              ?.idSchedaSoggettoComuneIstat || '',
          anprIdentifier: person.unioneCivile?.unitoCivilmente?.idSchedaSoggettoANPR || '',
          note: person.unioneCivile?.unitoCivilmente?.note || '',
        },
        person:
          person.unioneCivile?.unitoCivilmente?.codiceFiscale?.codFiscale ||
          person.unioneCivile?.unitoCivilmente?.nome ||
          person.unioneCivile?.unitoCivilmente?.cognome
            ? {
                fiscalCode: person.unioneCivile?.unitoCivilmente?.codiceFiscale?.codFiscale || '',
                name: person.unioneCivile?.unitoCivilmente?.nome || '',
                surname: person.unioneCivile?.unitoCivilmente?.cognome || '',
                withoutName: person.unioneCivile?.unitoCivilmente?.senzaNome === '1' ? true : false,
                withoutSurname:
                  person.unioneCivile?.unitoCivilmente?.senzaCognome === '1' ? true : false,
              }
            : undefined,
        from: person.unioneCivile?.cittadinanza?.dataValidita
          ? DateTime.fromISO(person.unioneCivile?.cittadinanza?.dataValidita)
          : null,
        citizenshipCode: person.unioneCivile?.cittadinanza?.codiceStato || '',
        citizenship: person.unioneCivile?.cittadinanza?.codiceStato
          ? {
              id: person.unioneCivile?.cittadinanza?.codiceStato || '',
              value: person.unioneCivile?.cittadinanza?.descrizioneStato || '',
            }
          : undefined,
        note: person.unioneCivile?.cittadinanza?.noteStato || '',
        italian:
          person.unioneCivile?.attoUnione?.luogoEvento?.comune?.codiceIstat ||
          person.unioneCivile?.attoUnione?.luogoEvento?.comune?.nomeComune ||
          person.unioneCivile?.attoUnione?.luogoEvento?.comune?.siglaProvinciaIstat ||
          person.unioneCivile?.attoUnione?.luogoEvento?.comune?.descrizioneLocalita
            ? {
                location:
                  person.unioneCivile?.attoUnione?.luogoEvento?.comune?.descrizioneLocalita || '',
                municipality:
                  person.unioneCivile?.attoUnione?.luogoEvento?.comune?.codiceIstat ||
                  person.unioneCivile?.attoUnione?.luogoEvento?.comune?.nomeComune ||
                  person.unioneCivile?.attoUnione?.luogoEvento?.comune?.siglaProvinciaIstat
                    ? {
                        id: person.unioneCivile?.attoUnione?.luogoEvento?.comune?.codiceIstat || '',
                        description:
                          person.unioneCivile?.attoUnione?.luogoEvento?.comune?.nomeComune || '',
                        province:
                          person.unioneCivile?.attoUnione?.luogoEvento?.comune
                            ?.siglaProvinciaIstat || '',
                      }
                    : null,
                province:
                  person.unioneCivile?.attoUnione?.luogoEvento?.comune?.siglaProvinciaIstat || '',
              }
            : undefined,
        foreign:
          person.unioneCivile?.attoUnione?.luogoEvento?.localita?.descrizioneLocalita ||
          person.unioneCivile?.attoUnione?.luogoEvento?.localita?.provinciaContea ||
          person.unioneCivile?.attoUnione?.luogoEvento?.localita?.codiceStato ||
          person.unioneCivile?.attoUnione?.luogoEvento?.localita?.descrizioneStato
            ? {
                municipality:
                  person.unioneCivile?.attoUnione?.luogoEvento?.localita?.descrizioneLocalita ||
                  person.unioneCivile?.attoUnione?.luogoEvento?.localita?.provinciaContea
                    ? {
                        description:
                          person.unioneCivile?.attoUnione?.luogoEvento?.localita
                            ?.descrizioneLocalita || '',
                        province:
                          person.unioneCivile?.attoUnione?.luogoEvento?.localita?.provinciaContea ||
                          '',
                      }
                    : undefined,
                province:
                  person.unioneCivile?.attoUnione?.luogoEvento?.localita?.provinciaContea || '',
                state:
                  person.unioneCivile?.attoUnione?.luogoEvento?.localita?.codiceStato ||
                  person.unioneCivile?.attoUnione?.luogoEvento?.localita?.descrizioneStato
                    ? {
                        id:
                          person.unioneCivile?.attoUnione?.luogoEvento?.localita?.codiceStato || '',
                        value:
                          person.unioneCivile?.attoUnione?.luogoEvento?.localita
                            ?.descrizioneStato || '',
                      }
                    : undefined,
              }
            : undefined,
        isForeign: !!person.unioneCivile?.attoUnione?.luogoEvento?.localita,
        eventDate: person.unioneCivile?.attoUnione?.dataEvento
          ? DateTime.fromISO(person.unioneCivile?.attoUnione?.dataEvento)
          : null,
        eventChoiceDate,
        exceptionalLocation: person.unioneCivile?.attoUnione?.luogoEvento?.luogoEccezionale || '',
        isDigital,
        digital: isDigital
          ? {
              documentDate: person.unioneCivile?.attoUnione?.attoEventoANPR?.attoANSC
                ?.dataFormazioneAtto
                ? DateTime.fromISO(
                    person.unioneCivile?.attoUnione?.attoEventoANPR?.attoANSC?.dataFormazioneAtto
                  )
                : null,
              idAnsc: person.unioneCivile?.attoUnione?.attoEventoANPR?.attoANSC?.idANSC || '',
              istatCode:
                person.unioneCivile?.attoUnione?.attoEventoANPR?.attoANSC?.comuneRegistrazione
                  ?.codiceIstat || '',
              municipality: {
                id:
                  person.unioneCivile?.attoUnione?.attoEventoANPR?.attoANSC?.comuneRegistrazione
                    ?.codiceIstat || '',
                description:
                  person.unioneCivile?.attoUnione?.attoEventoANPR?.attoANSC?.comuneRegistrazione
                    ?.nomeComune || '',
                province:
                  person.unioneCivile?.attoUnione?.attoEventoANPR?.attoANSC?.comuneRegistrazione
                    ?.siglaProvinciaIstat || '',
              },
              numberMunicipality:
                person.unioneCivile?.attoUnione?.attoEventoANPR?.attoANSC?.numeroComunale || '',
              year: person.unioneCivile?.attoUnione?.attoEventoANPR?.attoANSC?.anno || '',
              transcript:
                person.unioneCivile?.attoUnione?.attoEventoANPR?.attoANSC?.trascritto === '1'
                  ? true
                  : false,
            }
          : undefined,
        // paper
        municipality: person.unioneCivile?.attoUnione?.attoEventoANPR?.atto?.comuneRegistrazione
          ? {
              id:
                person.unioneCivile?.attoUnione?.attoEventoANPR?.atto?.comuneRegistrazione
                  ?.codiceIstat || '',
              description:
                person.unioneCivile?.attoUnione?.attoEventoANPR?.atto?.comuneRegistrazione
                  ?.nomeComune || '',
              province:
                person.unioneCivile?.attoUnione?.attoEventoANPR?.atto?.comuneRegistrazione
                  ?.siglaProvinciaIstat || '',
            }
          : undefined,
        // municipality:
        // person.unioneCivile?.attoUnione?.attoEventoANPR?.atto?.comuneRegistrazione?.nomeComune || '',
        year: person.unioneCivile?.attoUnione?.attoEventoANPR?.atto?.anno || '',
        province:
          person.unioneCivile?.attoUnione?.attoEventoANPR?.atto?.comuneRegistrazione
            ?.siglaProvinciaIstat || '',
        documentNumber: person.unioneCivile?.attoUnione?.attoEventoANPR?.atto?.numeroAtto || '',
        portion: person.unioneCivile?.attoUnione?.attoEventoANPR?.atto?.parte || '',
        range: person.unioneCivile?.attoUnione?.attoEventoANPR?.atto?.serie || '',
        regVol: person.unioneCivile?.attoUnione?.attoEventoANPR?.atto?.volume || '',
        office: person.unioneCivile?.attoUnione?.attoEventoANPR?.atto?.ufficioMunicipio || '',
        documentDate: person.unioneCivile?.attoUnione?.attoEventoANPR?.atto?.dataFormazioneAtto
          ? DateTime.fromISO(
              person.unioneCivile?.attoUnione?.attoEventoANPR?.atto?.dataFormazioneAtto
            )
          : null,
        transcript:
          person.unioneCivile?.attoUnione?.attoEventoANPR?.atto?.trascritto === '1' ? true : false,
      }
    : undefined;

  return originReturnData;
};
